import React, { useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Icon, Menu, MenuItem, Tooltip } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import cx from 'classnames';
import { NotificationManager } from 'Components/Notifications';
import { translateEvent } from 'Modules/.common/utils/translateEvents';
import {
    FRESH_ROW_MS, LOG_POLL_MS, LOG_RANGES, countTransmissions, formatDay, formatStamp, formatTime, httpStatusText, rangeBounds,
    rangeDays, safeTimeZone, transmissionKey, transmissionsCsvLabels, transmissionsFileName, zoneLabel
} from '../../transmissions';
// Actions
import { accountAppsExportTransmissions, accountAppsSearchTransmissions, openAppsLog, setAppsLogFilter } from '../../redux/slice';
import './styles.scss';

/**
 * Transmission Log side panel of the linked vehicles console (design, shared panel): the latest transmissions
 * of the integration, or of one vehicle, for Today / Yesterday / Last 7 Days, all or errors only, with the
 * error details on click and a CSV of the whole range. On a live range it polls for new rows.
 * Rows: searchCompanyIntegrationTransmissions; counts: the console's daily summaries (account days).
 */
const TransmissionLog = ({ intl: { messages }, integration, units, stats }) => {
    const dispatch = useDispatch();
    const { log } = useSelector((state) => state.accountAppsRedux);
    const [rangeAnchor, setRangeAnchor] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const rowsRef = useRef(log.rows);
    rowsRef.current = log.rows;

    const integrationId = integration.id;
    // The filters live in the slice: a late answer of a previous filter is dropped there
    const { unitId, errorsOnly, range } = log;
    const timeZone = safeTimeZone(stats?.timeZone);
    const unitsById = useMemo(() => Object.fromEntries((units || []).filter((unit) => unit.unitId).map((unit) => [unit.unitId, unit])), [units]);
    const tr = (key, values = {}) => Object.entries({ _APP_: integration.integrationName, ...values })
        .reduce((text, [placeholder, value]) => text.split(placeholder).join(value), messages[key] || '');

    const search = (mode, from, to) => dispatch(accountAppsSearchTransmissions({ companyIntegrationId: integrationId, unitId, errorsOnly, range, from, to, mode }));

    // The latest rows of the range, then only the ones newer than the panel shows while the range is live
    useEffect(() => {
        setExpanded(null);
        const { from, to } = rangeBounds(range, timeZone);
        search('replace', from, to);
    }, [integrationId, range, unitId, errorsOnly, timeZone]);

    useEffect(() => {
        if (!rangeBounds(range, timeZone).live) return undefined;
        const poll = setInterval(() => {
            const newest = rowsRef.current[0]?.createdOn;
            search('prepend', newest || rangeBounds(range, timeZone).from, new Date().toISOString());
        }, LOG_POLL_MS);
        const clock = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            clearInterval(poll);
            clearInterval(clock);
        };
    }, [integrationId, range, unitId, errorsOnly, timeZone]);

    const close = () => dispatch(openAppsLog({ open: false }));

    // Counts come from the daily summaries of the account days in the range; the rows on screen never count less
    // (transmissions written before the summaries existed have none)
    const days = rangeDays(range, stats?.days);
    const summary = countTransmissions(stats?.items, days, unitId);
    const shown = log.rows.length;
    const shownErrors = log.rows.filter((row) => row.status === 'ERROR').length;
    const counts = { all: Math.max(summary.all, shown), errors: Math.max(summary.errors, shownErrors) };
    const rangeCount = (key) => countTransmissions(stats?.items, rangeDays(key, stats?.days)).all;
    const rangeLabel = messages[`accountAppsLog_range_${range}`];
    const unitLabel = unitId ? unitsById[unitId]?.unitLabel || `#${unitId}` : null;
    const total = errorsOnly ? counts.errors : counts.all;
    const footer = log.loading || log.error ? ''
        : log.hasMore || total > shown ? tr('accountAppsLog_footerMore', { _SHOWN_: shown, _TOTAL_: total.toLocaleString() })
            : !shown ? ''
                : tr('accountAppsLog_footerCount', { _COUNT_: total.toLocaleString(), _ERRORS_: counts.errors.toLocaleString() });
    const downloadTip = tr(errorsOnly ? 'accountAppsLog_downloadErrors' : 'accountAppsLog_download', { _RANGE_: rangeLabel });

    const exportCsv = async () => {
        if (log.exporting) return;
        const { from, to } = rangeBounds(range, timeZone);
        const fileName = transmissionsFileName(integration.integrationCode, range, unitLabel, errorsOnly);
        try {
            const labels = transmissionsCsvLabels(messages, { appName: integration.integrationName, timeZone });
            const result = await dispatch(accountAppsExportTransmissions({ companyIntegrationId: integrationId, unitId, errorsOnly, from, to, timeZone, unitsById, labels, fileName })).unwrap();
            if (!result.rows) {
                NotificationManager.info(messages['accountAppsLog_exportEmpty'], messages['accountAppsLog_title'], 4000, null, null, 'info');
            } else if (result.truncated) {
                NotificationManager.error(tr('accountAppsLog_exportTruncated', { _COUNT_: result.rows.toLocaleString() }), messages['accountAppsLog_title'], 8000, null, null, 'warning');
            } else {
                NotificationManager.success(tr('accountAppsLog_exported', { _COUNT_: result.rows.toLocaleString() }), messages['success'], 3500, null, null, 'success');
            }
        } catch (e) {
            NotificationManager.error(messages['accountAppsLog_exportError'], messages['error'], 5000, null, null, 'warning');
        }
    };

    const multiDay = range === 'last7days';
    const renderRow = (row) => {
        const key = transmissionKey(row);
        const error = row.status === 'ERROR';
        const retry = !error && row.attempts > 1;
        const fresh = !!row.receivedAt && now - row.receivedAt < FRESH_ROW_MS;
        const open = expanded === key;
        const unit = unitsById[row.unitId];
        const code = row.httpStatus;
        const statusText = httpStatusText(code);
        const latency = row.durationMs === null || row.durationMs === undefined ? '—' : row.durationMs;
        const response = error ? `${code ? `${code} ` : ''}${messages['accountAppsLog_error']}`
            : retry ? `${code ? `${code} · ` : ''}${messages['accountAppsLog_retry']}`
                : `${code ? `${code} ` : ''}${messages['accountAppsLog_ok']}`;
        const responseTip = error ? (statusText ? tr('accountAppsLog_errorCodeTip', { _STATUS_: statusText }) : messages['accountAppsLog_errorTip'])
            : retry ? tr('accountAppsLog_deliveredRetryTip', { _MS_: latency, _N_: row.attempts })
                : tr('accountAppsLog_deliveredTip', { _MS_: latency });
        const hasCoords = [row.latitude, row.longitude].every((value) => value !== null && value !== undefined);
        const meta = [
            hasCoords ? `${Number(row.latitude).toFixed(4)}, ${Number(row.longitude).toFixed(4)}` : null,
            row.speedMph === null || row.speedMph === undefined ? null : tr('accountAppsLog_speed', { _MPH_: Math.round(row.speedMph) }),
            row.odometer === null || row.odometer === undefined ? null : tr('accountAppsLog_odometer', { _MI_: Number(row.odometer).toLocaleString() })
        ].filter(Boolean);
        return (
            <div key={key} className={cx('log-entry', { 'is-fresh': fresh, 'is-open': open })}>
                <div
                    className={cx('log-row', { 'is-error': error })}
                    role="row"
                    onClick={() => { if (error) setExpanded(open ? null : key); }}
                >
                    <div className="log-time">{multiDay ? `${formatDay(row.createdOn, timeZone)} · ` : ''}{formatTime(row.createdOn, timeZone)}</div>
                    <Tooltip title={unit?.unitLabel || ''} placement="top">
                        <div className="log-unit-cell">{unit?.unitLabel || (row.unitId ? `#${row.unitId}` : '—')}</div>
                    </Tooltip>
                    <div className="log-event">
                        {translateEvent(row.event, messages) || messages['accountAppsLog_eventDefault']}
                        {meta.length > 0 && <span className="log-meta"> · {meta.join(' · ')}</span>}
                    </div>
                    <div className="log-response">
                        <Tooltip title={responseTip} placement="top">
                            <span className={cx('log-response-pill', { 'is-error': error, 'is-retry': retry, 'is-ok': !error && !retry })}>{response}</span>
                        </Tooltip>
                        {error && <Icon className="log-chevron">{open ? 'expand_less' : 'expand_more'}</Icon>}
                    </div>
                </div>
                {
                    open &&
                    <div className="log-details">
                        <div className="log-details-title">
                            <Icon>error</Icon>
                            <div>
                                <div className="log-details-code">{statusText || messages['accountAppsLog_noResponse']}</div>
                                {row.errorMessage && <div className="log-details-message">{row.errorMessage}</div>}
                            </div>
                        </div>
                        {row.endpoint && <div><div className="log-details-label">{messages['accountAppsLog_endpoint']}</div><div className="log-details-mono">{row.endpoint}</div></div>}
                        <div><div className="log-details-label">{messages['accountAppsLog_attempts']}</div><div>{row.attempts ?? '—'}{row.attempts > 1 && <span className="log-details-note"> {messages['accountAppsLog_retriedBackoff']}</span>}</div></div>
                        <div><div className="log-details-label">{messages['accountAppsLog_latency']}</div><div>{latency} ms</div></div>
                        <div><div className="log-details-label">{messages['accountAppsLog_vin']}</div><div className="log-details-mono">{unit?.vin || '—'}</div></div>
                        <div><div className="log-details-label">{messages['accountAppsLog_readingTime']}</div><div className="log-details-mono">{row.unitTime ? formatStamp(row.unitTime, timeZone) : '—'}</div></div>
                    </div>
                }
            </div>
        );
    };

    return (
        <Drawer
            anchor="right"
            open
            onClose={close}
            className="account-apps-log"
            PaperProps={{ className: 'account-apps-log-paper', role: 'dialog', 'aria-label': messages['accountAppsLog_title'] }}
        >
            <header className="log-header">
                <div className="log-heading">
                    <span className="log-title">{messages['accountAppsLog_title']}</span>
                    <span className="log-sub">{tr('accountAppsLog_sub', { _TZ_: zoneLabel(timeZone) })}</span>
                </div>
                <Tooltip title={messages['accountAppsLog_close']} placement="bottom">
                    <button type="button" className="log-close" aria-label={messages['accountAppsLog_close']} onClick={close}>
                        <Icon>close</Icon>
                    </button>
                </Tooltip>
            </header>

            <div className="log-toolbar">
                <Tooltip title={downloadTip} placement="bottom">
                    <span>
                        <button type="button" className="log-download" aria-label={downloadTip} disabled={log.exporting} onClick={exportCsv}>
                            <Icon className={cx({ 'log-spin': log.exporting })}>{log.exporting ? 'autorenew' : 'download'}</Icon>
                        </button>
                    </span>
                </Tooltip>
                <button
                    type="button"
                    className="log-range"
                    aria-haspopup="listbox"
                    aria-expanded={!!rangeAnchor}
                    onClick={(e) => setRangeAnchor(e.currentTarget)}
                >
                    {rangeLabel}
                    <Icon className="log-range-arrow">arrow_drop_down</Icon>
                </button>
                <Menu
                    anchorEl={rangeAnchor}
                    open={!!rangeAnchor}
                    onClose={() => setRangeAnchor(null)}
                    className="account-apps-log-ranges"
                    MenuListProps={{ role: 'listbox' }}
                >
                    {LOG_RANGES.map((key) => (
                        <MenuItem
                            key={key}
                            role="option"
                            aria-selected={key === range}
                            selected={key === range}
                            className="log-range-option"
                            onClick={() => { dispatch(setAppsLogFilter({ range: key })); setRangeAnchor(null); }}
                        >
                            {messages[`accountAppsLog_range_${key}`]}
                            <span className="log-range-count">{rangeCount(key).toLocaleString()}</span>
                        </MenuItem>
                    ))}
                </Menu>
                <div className="log-chips">
                    <button type="button" className={cx('log-chip', { 'is-selected': !errorsOnly })} aria-pressed={!errorsOnly} onClick={() => dispatch(setAppsLogFilter({ errorsOnly: false }))}>
                        {messages['accountAppsLog_all']} · {counts.all.toLocaleString()}
                    </button>
                    <button type="button" className={cx('log-chip log-chip-errors', { 'is-selected': errorsOnly })} aria-pressed={errorsOnly} onClick={() => dispatch(setAppsLogFilter({ errorsOnly: true }))}>
                        <Icon>error</Icon>
                        {messages['accountAppsLog_errors']} · {counts.errors.toLocaleString()}
                    </button>
                </div>
                {
                    unitId &&
                    <span className="log-unit">
                        <Tooltip title={unitLabel} placement="top"><span className="log-unit-label">{unitLabel}</span></Tooltip>
                        <Tooltip title={messages['accountAppsLog_clearUnit']} placement="top">
                            <button type="button" aria-label={messages['accountAppsLog_clearUnit']} onClick={() => dispatch(setAppsLogFilter({ unitId: null }))}>
                                <Icon>cancel</Icon>
                            </button>
                        </Tooltip>
                    </span>
                }
            </div>

            <div className="log-scroll">
                <div className="log-table" role="table">
                    <div className="log-row log-row-head" role="row">
                        <div>{messages['accountAppsLog_colTime']}</div>
                        <div>{messages['accountAppsLog_colUnit']}</div>
                        <div>{messages['accountAppsLog_colEvent']}</div>
                        <div>{messages['accountAppsLog_colResponse']}</div>
                    </div>
                    {log.rows.map(renderRow)}
                    {log.loading && !log.rows.length && <div className="log-loading"><Icon className="log-spin">autorenew</Icon></div>}
                    {
                        !log.loading && log.error &&
                        <div role="alert" className="log-error">
                            {messages['accountAppsLog_loadError']}
                            <button type="button" onClick={() => { const { from, to } = rangeBounds(range, timeZone); search('replace', from, to); }}>{messages['accountAppsWizard_retry']}</button>
                        </div>
                    }
                    {
                        !log.loading && !log.error && !log.rows.length &&
                        <div className="log-empty">
                            <InboxOutlinedIcon />
                            <div>{messages['accountAppsLog_empty']}</div>
                        </div>
                    }
                    <div className="log-footer">{footer}</div>
                </div>
            </div>
        </Drawer>
    );
};

export default injectIntl(TransmissionLog);
